import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Crown, Calendar, CreditCard, CheckCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { products } from '@/stripe-config';

interface SubscriptionData {
  order_status?: string;
  subscription_status?: string;
  price_id: string | null;
  order_date?: string | null;
  current_period_end?: number | null;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
  amount_total?: number | null;
  currency?: string | null;
}

export default function SubscriptionStatus() {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderStatus();
  }, []);

  const fetchOrderStatus = async () => {
    try {
      // Check for completed orders (one-time payments)
      const { data: orderData, error: orderError } = await supabase
        .from('stripe_user_orders')
        .select('*')
        .eq('order_status', 'completed')
        .order('order_date', { ascending: false })
        .maybeSingle();

      if (orderError) {
        console.error('Error fetching order:', orderError);
      } else if (orderData) {
        setSubscription({
          order_status: orderData.order_status,
          price_id: null, // Orders don't have price_id in the same way
          order_date: orderData.order_date,
          payment_method_brand: null,
          payment_method_last4: null,
          amount_total: orderData.amount_total,
          currency: orderData.currency
        });
        setLoading(false);
        return;
      }

      // Fallback: Check for subscriptions
      const { data: subData, error: subError } = await supabase
        .from('stripe_user_subscriptions')
        .select('*')
        .maybeSingle();

      if (subError) {
        console.error('Error fetching subscription:', subError);
      } else {
        setSubscription(subData);
      }
    } catch (error) {
      console.error('Error fetching payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 w-32 bg-neutral-200 rounded" />
      </div>
    );
  }

  // Show if user has completed an order or active subscription
  if (!subscription || (!subscription.order_status && !subscription.subscription_status)) {
    return (
      <Badge variant="outline" className="bg-neutral-100 text-neutral-700">
        Kostenlose Version
      </Badge>
    );
  }

  // Handle completed orders (one-time payments)
  if (subscription.order_status === 'completed') {
    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Crown className="h-4 w-4 text-yellow-600" />
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            ImmoIQ Premium
          </Badge>
        </div>
        
        {subscription.order_date && (
          <div className="text-xs text-neutral-600 flex items-center space-x-1">
            <Calendar className="h-3 w-3" />
            <span>
              Gekauft am: {new Date(subscription.order_date).toLocaleDateString('de-DE')}
            </span>
          </div>
        )}
        
        {subscription.amount_total && subscription.currency && (
          <div className="text-xs text-neutral-600">
            Bezahlt: {(subscription.amount_total / 100).toLocaleString('de-DE', {
              style: 'currency',
              currency: subscription.currency.toUpperCase()
            })}
          </div>
        )}
      </div>
    );
  }

  // Handle subscriptions (if any exist)
  const subscriptionStatus = subscription.subscription_status || 'not_started';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'trialing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'canceled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-neutral-100 text-neutral-700 border-neutral-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'trialing':
        return 'Testphase';
      case 'past_due':
        return 'Überfällig';
      case 'canceled':
        return 'Gekündigt';
      case 'incomplete':
        return 'Unvollständig';
      default:
        return status;
    }
  };

  const getProductName = (priceId: string | null) => {
    if (!priceId) return 'Unbekanntes Produkt';
    const product = products.find(p => p.priceId === priceId);
    return product?.name || 'Unbekanntes Produkt';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Crown className="h-4 w-4 text-yellow-600" />
        <Badge className={getStatusColor(subscriptionStatus)}>
          {getProductName(subscription.price_id)} - {getStatusLabel(subscriptionStatus)}
        </Badge>
      </div>
      
      {subscription.current_period_end && subscriptionStatus === 'active' && (
        <div className="text-xs text-neutral-600 flex items-center space-x-1">
          <Calendar className="h-3 w-3" />
          <span>
            Verlängert bis: {new Date(subscription.current_period_end * 1000).toLocaleDateString('de-DE')}
          </span>
        </div>
      )}
      
      {subscription.payment_method_brand && subscription.payment_method_last4 && (
        <div className="text-xs text-neutral-600 flex items-center space-x-1">
          <CreditCard className="h-3 w-3" />
          <span>
            {subscription.payment_method_brand.toUpperCase()} ****{subscription.payment_method_last4}
          </span>
        </div>
      )}
    </div>
  );
}